import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { registerLicense } from '@syncfusion/ej2-base';
/** Material theme for Syncfusion components (Ej2 v20.4.48 — align all @syncfusion packages to the same 20.4.x patch). */
import '@syncfusion/ej2-material-theme/styles/material.css';
import './index.css';
import './shared/styles/backoffice.css';
import { AppProviders } from './app/providers';
import { RouterProvider } from 'react-router-dom';
import { router } from './app/routes';
import { getMsalConfiguration, isAzureAuthConfigured } from './auth/msalConfig';
import reportWebVitals from './reportWebVitals';
import { initApplicationInsights } from './telemetry/appInsights';
import { AppErrorBoundary } from './shared/components/AppErrorBoundary';

registerLicense(process.env.REACT_APP_SYNCFUSION_LICENSE ?? '');
initApplicationInsights();

const azureAuthEnabled = isAzureAuthConfigured();

/**
 * True when the document is running inside a frame. MSAL renews tokens silently via a hidden iframe
 * pointed at the `redirectUri` (the SPA root); Azure Static Web Apps serves the full app there.
 * We must NOT bootstrap the SPA in that iframe, or its `acquireTokenSilent` would spawn another
 * hidden iframe, recursing into an `/authorize` storm that gets the account blocked by Entra.
 */
function isInAuthIframe(): boolean {
  try {
    return typeof window !== 'undefined' && window.self !== window.top;
  } catch {
    // Cross-origin access to `window.top` throws only when we are framed.
    return true;
  }
}

/** Only round-trip back to paths that exist in this app — never follow an arbitrary MSAL `state`. */
const RETURN_PATH_PATTERN = /^\/(admin|merchants)(\/[\w-]+)*\/?$/;

async function bootstrap(): Promise<void> {
  let msalInstance: PublicClientApplication | undefined;
  let returnPath: string | undefined;
  if (azureAuthEnabled) {
    msalInstance = new PublicClientApplication(getMsalConfiguration());
    await msalInstance.initialize();
    const redirectResult = await msalInstance.handleRedirectPromise();
    const state = redirectResult?.state;
    if (state && RETURN_PATH_PATTERN.test(state)) {
      returnPath = state;
    }
  }

  // Inside MSAL's silent-renewal iframe, let the parent window read the hash but never mount the
  // SPA (prevents the recursive iframe / token-acquisition loop).
  if (isInAuthIframe()) {
    return;
  }

  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

  const appTree = (
    <React.StrictMode>
      <AppProviders azureAuthEnabled={azureAuthEnabled}>
        <RouterProvider router={router} />
      </AppProviders>
    </React.StrictMode>
  );

  root.render(
    <AppErrorBoundary>
      {msalInstance ? (
        <MsalProvider instance={msalInstance}>{appTree}</MsalProvider>
      ) : (
        appTree
      )}
    </AppErrorBoundary>
  );

  // `redirectUri` always resolves to the bare origin (see msalConfig.ts), so a sign-in kicked off
  // from e.g. /admin/dashboard comes back to "/" — send the user back to where they started.
  if (returnPath) {
    void router.navigate(returnPath, { replace: true });
  }

  reportWebVitals();
}

void bootstrap().catch((err) => {
  console.error(err);
});
